import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Modal, Badge, Pagination, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';

const RetailerLedger = () => {
  const { retailerId, profileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const passedRetailerData = location.state?.retailerData;
  
  const [retailerDetails, setRetailerDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    
    return {
      startDate: today.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  });

  console.log(retailerDetails);


  useEffect(() => {
    if (retailerId) {
      // Set retailer details from passed data if available
      if (passedRetailerData) {
        setRetailerDetails(transformPassedData(passedRetailerData));
      }
    }
  }, [retailerId, passedRetailerData]);

  useEffect(() => {
    if (retailerId) {
      fetchTransactionHistory();
    }
  }, [retailerId, dateRange, currentPage, rowsPerPage, searchQuery]);

  // Transform passed retailer data to match expected format
  const transformPassedData = (retailer) => {
    return {
      customer_id: retailer.customerId,
      user_name: retailer.ownerName,
      user_mobile: retailer.mobile,
      user_shop_name: retailer.storeName,
      beat_name: retailer.beatName,
      outstanding_amount: retailer.totalOutstanding,
      credit_limit: retailer.creditLimit,
      operator: retailer.operator,
      user_city: retailer.city,
      user_district: retailer.district
    };
  };


  const fetchTransactionHistory = async () => {
    setLoading(true);
    try {
      const params = {
        retailer_id: retailerId,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        page: currentPage,
        limit: rowsPerPage
      };

      // Add search parameter if search query exists
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      const response = await distributorService.getRetailerTransactionHistory(params);
      
      if (response.success) {
        setTransactions(response.data.transactions || []);
        setTotalCount(response.data.count || 0);
      } else {
        toast.error('Failed to fetch transaction history');
      }
    } catch (error) {
      toast.error('Failed to fetch transaction history');
      console.error('Error fetching transaction history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when date range changes
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const exportToExcel = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      toast.warning('Please select date range for export');
      return;
    }

    setExportLoading(true);
    try {
      const params = {
        retailerId: retailerId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };

      const response = await distributorService.exportRetailerLedger(params);
      
      if (response.success) {
        // Create blob and download file
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Ledger exported successfully');
      } else {
        toast.error('Failed to export ledger');
      }
    } catch (error) {
      toast.error('Failed to export ledger');
      console.error('Error exporting ledger:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleTransactionClick = async (transaction) => {
    try {
      const response = await distributorService.getTransactionDetails(transaction.id);
      if (response.success) {
        setSelectedTransaction(response.data);
        setShowTransactionModal(true);
      } else {
        toast.error('Failed to fetch transaction details');
      }
    } catch (error) {
      toast.error('Failed to fetch transaction details');
      console.error('Error fetching transaction details:', error);
    }
  };

  const getTransactionTypeColor = (type) => {
    return type === 'CREDIT' ? 'text-success' : 'text-danger';
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <Container fluid className="py-1">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button 
            variant="outline-primary" 
            onClick={() => navigate('/distributor/collect/retailer-outstanding')}
            className="me-3"
            title="Back to Outstanding"
          >
            <i className="fas fa-arrow-left"></i>
          </Button>
          <h5 style={{ color: '#031a6f', fontWeight: '600', display: 'inline' }}>
            <i className="fas fa-book me-2"></i>
            Retailer Ledger
          </h5>
        </div>
        <Button variant="success" onClick={exportToExcel} disabled={exportLoading}>
          <i className="fas fa-file-excel me-2"></i>
          {exportLoading ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>

      {/* Retailer Info Section */}
      {(retailerDetails || passedRetailerData) && (
        <Row className="mb-4">
          <Col md={12}>
            <div className="d-flex gap-3 align-items-center bg-light p-3 rounded border">
              <div>
                <small className="text-muted d-block">ID</small>
                <strong>{retailerDetails?.customer_id || passedRetailerData?.customerId}</strong>
              </div>
              <div className="border-start ps-3">
                <small className="text-muted d-block">Shop Name</small>
                <strong>{retailerDetails?.user_shop_name || passedRetailerData?.storeName || 'N/A'}</strong>
              </div>
              <div className="border-start ps-3">
                <small className="text-muted d-block">Owner</small>
                <strong>{retailerDetails?.user_name || passedRetailerData?.ownerName || 'N/A'}</strong>
              </div>
              <div className="border-start ps-3">
                <small className="text-muted d-block">Mobile</small>
                <strong>{retailerDetails?.user_mobile || passedRetailerData?.mobile}</strong>
              </div>
              <div className="border-start ps-3">
                <small className="text-muted d-block">Beat</small>
                <strong className="text-info">{retailerDetails?.beat_name || passedRetailerData?.beatName || 'Not Assigned'}</strong>
              </div>
              <div className="border-start ps-3">
                <small className="text-muted d-block">Outstanding</small>
                <strong className="text-danger">₹{parseFloat(retailerDetails?.outstanding_amount || passedRetailerData?.totalOutstanding || 0).toLocaleString()}</strong>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Search</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search by Order ID, Amount, Mobile, etc..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <InputGroup.Text>
                <i className="fas fa-search"></i>
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>&nbsp;</Form.Label>
            <div>
              <Button 
                variant="primary" 
                onClick={fetchTransactionHistory} 
                disabled={loading} 
                title="Apply Filter"
                className="w-100"
              >
                <i className="fas fa-filter me-2"></i>
                Filter
              </Button>
            </div>
          </Form.Group>
        </Col>
      </Row>


      {/* Transactions Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
              <thead>
                <tr>
                  <th>Entry Date</th>
                  <th>Transaction Date</th>
                  <th>Order ID</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Closing Balance</th>
                  <th>Payment Mode</th>
                  <th>Collected By Name</th>
                  <th>Remarks</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((txn) => (
                    <tr key={txn.id}>
                      <td>
                        <div>
                          {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : 'N/A'}
                          <br />
                          <small className="text-muted">
                            {txn.created_at ? new Date(txn.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : ''}
                          </small>
                        </div>
                      </td>

                      <td>
                        <div>{txn.transaction_date ? new Date(txn.transaction_date).toLocaleDateString() : 'N/A'}</div>
                        <small className="text-muted">{txn.transaction_time ? new Date(`1970-01-01T${txn.transaction_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : ''}</small>
                      </td>
                      <td>
                        <small className="font-monospace">{txn.order_id}</small>
                      </td>
                      <td>
                        <small className="text-muted">{txn.txn_type}</small>
                      </td>
                      <td>
                        <span className={getTransactionTypeColor(txn.transaction_type)}>
                          <strong>{txn.transaction_type === 'CREDIT' ? '+' : '-'}₹{txn.amount?.toLocaleString()}</strong>
                        </span>
                      </td>
                      <td>₹{txn.closing_balance?.toLocaleString()}</td>
                      <td>{txn.payment_mode}</td>
                      <td>
                        {txn.collected_by_name || 'N/A'}
                      </td>
                      <td>
                        {txn.remarks || '-'}
                      </td>
                      <td>
                        {txn.source || 'N/A'}
                      </td>
                      
                    </tr>
                  ))
                )}
              </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {!loading && transactions.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="d-flex align-items-center gap-3">
            <div>
              <Form.Select
                value={rowsPerPage}
                onChange={(e) => handleRowsPerPageChange(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </Form.Select>
            </div>
            <div>
              Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
            </div>
          </div>
          <Pagination>
            <Pagination.First 
              onClick={() => setCurrentPage(1)} 
              disabled={currentPage === 1} 
            />
            <Pagination.Prev 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
              disabled={currentPage === 1} 
            />
            {(() => {
              const pages = [];
              const maxVisiblePages = 5;
              
              let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
              let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
              
              if (endPage - startPage < maxVisiblePages - 1) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }
              
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i}
                  </Pagination.Item>
                );
              }
              
              return pages;
            })()}
            <Pagination.Next 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
              disabled={currentPage === totalPages} 
            />
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)} 
              disabled={currentPage === totalPages} 
            />
          </Pagination>
        </div>
      )}

      {/* Transaction Details Modal */}
      <Modal show={showTransactionModal} onHide={() => setShowTransactionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Transaction Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTransaction && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="text-muted">Transaction ID</h6>
                  <p className="fw-bold">{selectedTransaction.id}</p>
                </Col>
                <Col md={6}>
                  <h6 className="text-muted">Order ID</h6>
                  <p className="fw-bold">{selectedTransaction.order_id}</p>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="text-muted">Transaction Date</h6>
                  <p className="fw-bold">
                    {new Date(selectedTransaction.transaction_date).toLocaleDateString()}
                  </p>
                </Col>
                <Col md={6}>
                  <h6 className="text-muted">Transaction Time</h6>
                  <p className="fw-bold">{new Date(`1970-01-01T${selectedTransaction.transaction_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="text-muted">Transaction Type</h6>
                  <Badge bg={selectedTransaction.transaction_type === 'CREDIT' ? 'success' : 'danger'}>
                    {selectedTransaction.transaction_type}
                  </Badge>
                </Col>
                <Col md={6}>
                  <h6 className="text-muted">Transaction SubType</h6>
                  <p className="fw-bold">{selectedTransaction.txn_type}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <h6 className="text-muted">Amount</h6>
                  <p className={`fw-bold ${selectedTransaction.transaction_type === 'CREDIT' ? 'text-success' : 'text-danger'}`}>
                    {selectedTransaction.transaction_type === 'CREDIT' ? '+' : '-'}₹{selectedTransaction.amount?.toLocaleString()}
                  </p>
                </Col>
                <Col md={4}>
                  <h6 className="text-muted">Opening Balance</h6>
                  <p className="fw-bold">₹{selectedTransaction.opening_balance?.toLocaleString()}</p>
                </Col>
                <Col md={4}>
                  <h6 className="text-muted">Closing Balance</h6>
                  <p className="fw-bold">₹{selectedTransaction.closing_balance?.toLocaleString()}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="text-muted">Payment Mode</h6>
                  <p className="fw-bold">{selectedTransaction.payment_mode}</p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="text-muted">Collected By</h6>
                  <p className="fw-bold">
                    {selectedTransaction.collected_by_name || 'N/A'}
                    <br />
                    <small className="text-muted">{selectedTransaction.collected_by_mobile || '-'}</small>
                  </p>
                </Col>
                <Col md={6}>
                  <h6 className="text-muted">Created At</h6>
                  <p className="fw-bold">
                    {selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : 'N/A'}
                  </p>
                </Col>
              </Row>

              {selectedTransaction.notes && (
                <Row className="mb-3">
                  <Col md={12}>
                    <h6 className="text-muted">Notes</h6>
                    <p className="fw-bold">{selectedTransaction.notes}</p>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransactionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RetailerLedger;